import {
  af as afDateFns,
  de as deDateFns,
  enUS,
  es as esDateFns,
  fr as frDateFns,
} from "date-fns/locale";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import resources from "./locales";

// Languages the user can pick from. Add an entry here (and its resources in
// src/locales/index.js) to make a new language selectable.
// `label` is the language's own name (shown untranslated in the picker);
// `locale` is used for date formatting; `dateFns` is its date-fns locale.
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", locale: "en-GB", dateFns: enUS },
  { code: "fr", label: "Français", locale: "fr-FR", dateFns: frDateFns },
  { code: "es", label: "Español", locale: "es-ES", dateFns: esDateFns },
  { code: "de", label: "Deutsch", locale: "de-DE", dateFns: deDateFns },
  { code: "af", label: "Afrikaans", locale: "af-ZA", dateFns: afDateFns },
];

export const DEFAULT_LANGUAGE = "en";

const LANGUAGE_STORAGE_KEY = "selectedLanguage";

const isSupported = (code) =>
  SUPPORTED_LANGUAGES.some((language) => language.code === code);

const readStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isSupported(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

i18n.use(initReactI18next).init({
  resources,
  lng: readStoredLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES.map((language) => language.code),
  interpolation: {
    escapeValue: false, // react already safes from xss
  },
  returnNull: false,
});

const syncDocumentLanguage = (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
};

syncDocumentLanguage(i18n.language);

i18n.on("languageChanged", (lng) => {
  syncDocumentLanguage(lng);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch {
    // Storage unavailable (private mode etc.) — language still applies.
  }
});

// Switch the app language. Ignores codes that aren't in SUPPORTED_LANGUAGES.
export const setLanguage = (code) => {
  if (!isSupported(code)) return Promise.resolve();
  return i18n.changeLanguage(code);
};

const currentLanguage = () =>
  SUPPORTED_LANGUAGES.find((language) => language.code === i18n.language) ||
  SUPPORTED_LANGUAGES[0];

export const isEnglish = () => currentLanguage().code === DEFAULT_LANGUAGE;

// Locale for toLocaleDateString / toLocaleTimeString / Intl.DateTimeFormat.
// In English each call site keeps the format it was designed with (pass that
// locale, e.g. "en-US"); other languages use their own conventions.
export const dateLocale = (englishLocale) =>
  isEnglish() ? englishLocale : currentLanguage().locale;

// date-fns `locale` option for format() and friends.
export const dateFnsLocale = () => currentLanguage().dateFns;

// Translate outside of React components (utils, toasts, constants).
// Always call at display time, never at module load, so the active language
// is respected.
export const t = (...args) => i18n.t(...args);

export default i18n;
